<template>
  <div class="pl-page">
    <div class="pl-container">

      <div class="pl-header">
        <div class="d-flex align-items-center">
          <button class="pl-back" @click="$router.push('/xisobot/main')" type="button">
            <b-icon icon="chevron-left"></b-icon>
          </button>
          <h2 class="pl-title">
            <b-icon icon="graph-up-arrow" class="mr-2"></b-icon>
            {{ $t("Umumiy foyda zarar") }}
          </h2>
        </div>
        <div class="pl-actions">
          <button class="pl-btn pl-btn-secondary" @click="doPrint" :disabled="loading">
            <b-icon icon="printer"></b-icon> Pechat
          </button>
        </div>
      </div>

      <div class="pl-filters">
        <div class="pl-filter">
          <label>Boshlanish</label>
          <input type="date" v-model="date1" @change="fetch">
        </div>
        <div class="pl-filter">
          <label>Tugash</label>
          <input type="date" v-model="date2" @change="fetch">
        </div>
        <div class="pl-filter pl-filter-grow">
          <label>Sklad</label>
          <v-select :options="skladList" v-model="sklad_id" :reduce="s => s.id" label="name" placeholder="Barchasi" @input="fetch" />
        </div>
        <div class="pl-filter">
          <label>&nbsp;</label>
          <button class="pl-btn pl-btn-primary" @click="fetch" :disabled="loading">
            <b-icon icon="arrow-repeat"></b-icon> Yangilash
          </button>
        </div>
      </div>

      <!-- KEY METRICS -->
      <div class="pl-summary">
        <div class="pl-metric pl-1">
          <div class="pl-metric-icon"><b-icon icon="arrow-down-circle-fill"></b-icon></div>
          <div class="pl-metric-body">
            <div class="pl-metric-label">JAMI KIRIM</div>
            <div class="pl-metric-value">{{ fmt(totals.total_income) }}</div>
          </div>
        </div>
        <div class="pl-metric pl-2">
          <div class="pl-metric-icon"><b-icon icon="arrow-up-circle-fill"></b-icon></div>
          <div class="pl-metric-body">
            <div class="pl-metric-label">JAMI CHIQIM</div>
            <div class="pl-metric-value">{{ fmt(totals.total_expense) }}</div>
          </div>
        </div>
        <div class="pl-metric" :class="netClass">
          <div class="pl-metric-icon"><b-icon :icon="totals.net_profit >= 0 ? 'graph-up' : 'graph-down'"></b-icon></div>
          <div class="pl-metric-body">
            <div class="pl-metric-label">SOF FOYDA / ZARAR</div>
            <div class="pl-metric-value">{{ totals.net_profit >= 0 ? '+ ' : '- ' }}{{ fmt(Math.abs(totals.net_profit)) }}</div>
          </div>
        </div>
        <div class="pl-metric pl-3">
          <div class="pl-metric-icon"><b-icon icon="box-seam-fill"></b-icon></div>
          <div class="pl-metric-body">
            <div class="pl-metric-label">MAHSULOT FOYDA (sotuv-tannarx)</div>
            <div class="pl-metric-value">{{ fmt(totals.gross_profit) }}</div>
          </div>
        </div>
      </div>

      <!-- CHART -->
      <div class="pl-chart-wrap" v-if="daily.length > 0">
        <h3 class="pl-block-title">Kunlik kirim vs chiqim</h3>
        <apexchart type="line" height="320" :options="lineChartOptions" :series="lineChartSeries"></apexchart>
      </div>

      <div class="pl-2col">
        <!-- Pie chart -->
        <div class="pl-chart-wrap" v-if="totals.total_income > 0 || totals.total_expense > 0">
          <h3 class="pl-block-title">Tuzilma</h3>
          <apexchart type="donut" height="280" :options="donutOptions" :series="donutSeries"></apexchart>
        </div>

        <!-- Mahsulot foyda detail -->
        <div class="pl-section" v-if="totals.total_sales > 0">
          <h3 class="pl-block-title">Mahsulot foyda tafsiloti</h3>
          <table class="pl-mini">
            <tr><td>Sotuv summasi</td><td class="t-right">{{ fmt(totals.total_sales) }}</td></tr>
            <tr><td>Tannarx</td><td class="t-right pl-red">- {{ fmt(totals.total_cost) }}</td></tr>
            <tr class="pl-mini-total"><td><b>Sof foyda (mahsulot)</b></td><td class="t-right pl-green"><b>{{ fmt(totals.gross_profit) }}</b></td></tr>
            <tr v-if="totals.total_sales > 0">
              <td>Foyda %</td>
              <td class="t-right pl-strong">{{ ((totals.gross_profit / totals.total_sales) * 100).toFixed(2) }}%</td>
            </tr>
          </table>
        </div>
      </div>

      <!-- DOC TYPE BO'YICHA -->
      <div class="pl-section">
        <h3 class="pl-block-title">Hujjat turlari kesimida</h3>
        <div class="pl-table-wrap">
          <table class="pl-table">
            <thead>
              <tr>
                <th>Hujjat turi</th>
                <th class="t-center">Soni</th>
                <th class="t-right">Kirim</th>
                <th class="t-right">Chiqim</th>
                <th class="t-right">Sof</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in byDocType" :key="i">
                <td class="pl-strong">{{ r.doc_type || '—' }}</td>
                <td class="t-center"><span class="pl-badge">{{ r.doc_count }}</span></td>
                <td class="t-right pl-green">{{ fmt(r.income) }}</td>
                <td class="t-right pl-red">{{ fmt(r.expense) }}</td>
                <td class="t-right" :class="(r.income - r.expense) >= 0 ? 'pl-green' : 'pl-red'">
                  {{ fmt(r.income - r.expense) }}
                </td>
              </tr>
              <tr v-if="byDocType.length === 0">
                <td colspan="5" class="pl-empty">Ma'lumot yo'q</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'ProfitLossReport',
  data() {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      date1: this.dateStr(first),
      date2: this.dateStr(today),
      sklad_id: null,
      skladList: [],
      byDocType: [],
      daily: [],
      totals: { total_income: 0, total_expense: 0, net_profit: 0, gross_profit: 0, total_sales: 0, total_cost: 0 },
      loading: false,
    };
  },
  computed: {
    netClass() {
      return this.totals.net_profit >= 0 ? 'pl-net-green' : 'pl-net-red';
    },
    lineChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'inherit' },
        colors: ['#16a34a', '#e11d48', '#a142f4'],
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 4 },
        xaxis: { categories: this.daily.map(d => d.day), labels: { style: { fontSize: '11px' } } },
        yaxis: { labels: { formatter: v => this.fmt(v) } },
        dataLabels: { enabled: false },
        legend: { position: 'top' },
        grid: { borderColor: '#f1f5f9' },
        tooltip: { y: { formatter: v => this.fmt(v) + " so'm" } }
      };
    },
    lineChartSeries() {
      return [
        { name: 'Kirim', data: this.daily.map(d => parseFloat(d.income) || 0) },
        { name: 'Chiqim', data: this.daily.map(d => parseFloat(d.expense) || 0) },
        { name: 'Sof', data: this.daily.map(d => (parseFloat(d.income) || 0) - (parseFloat(d.expense) || 0)) },
      ];
    },
    donutOptions() {
      return {
        chart: { fontFamily: 'inherit' },
        colors: ['#16a34a', '#e11d48'],
        labels: ['Kirim', 'Chiqim'],
        legend: { position: 'bottom' },
        dataLabels: {
          enabled: true,
          formatter: (v) => v.toFixed(1) + '%'
        },
        plotOptions: { pie: { donut: { size: '60%' } } }
      };
    },
    donutSeries() {
      return [parseFloat(this.totals.total_income) || 0, parseFloat(this.totals.total_expense) || 0];
    }
  },
  mounted() {
    this.loadSklads();
    this.fetch();
  },
  methods: {
    dateStr(d) {
      const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    toUnix(s, end = false) {
      const d = new Date(s + (end ? 'T23:59:59' : 'T00:00:00'));
      return Math.floor(d.getTime() / 1000);
    },
    fmt(v) {
      const n = parseFloat(v) || 0;
      return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    },
    async loadSklads() {
      try { const res = await axios.get('api/v1/sklads'); this.skladList = res.data || []; } catch (e) { this.skladList = []; }
    },
    async fetch() {
      this.loading = true;
      try {
        const res = await axios.post('api/v1/report/profit-loss/', {
          datetime1: this.toUnix(this.date1, false),
          datetime2: this.toUnix(this.date2, true),
          sklad_id: this.sklad_id || null,
        });
        const d = res.data || {};
        this.byDocType = d.by_doc_type || [];
        this.daily = d.daily || [];
        this.totals = d.totals || this.totals;
      } catch (e) {
        console.error('Profit-loss err:', e);
      } finally { this.loading = false; }
    },
    doPrint() { window.print(); }
  }
};
</script>

<style scoped>
.pl-page { padding: 20px; background: #f4f7f9; height: calc(100vh - 56px); overflow-y: auto; }
.pl-container { max-width: 1400px; margin: 0 auto; }
.pl-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; }
.pl-back { width: 40px; height: 40px; border-radius: 50%; border: 1px solid #e0e6ed; background: white; color: #7f8c8d; display: flex; align-items: center; justify-content: center; cursor: pointer; margin-right: 12px; }
.pl-title { font-weight: 800; font-size: 22px; color: #0f172a; margin: 0; display: flex; align-items: center; }
.pl-actions { display: flex; gap: 10px; }
.pl-btn { border: none; padding: 9px 18px; border-radius: 10px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.pl-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pl-btn-primary { background: #a142f4; color: white; }
.pl-btn-secondary { background: white; color: #475569; border: 1px solid #cbd5e1; }

.pl-filters { display: flex; gap: 12px; background: white; padding: 14px; border-radius: 14px; margin-bottom: 16px; border: 1px solid #f1f5f9; align-items: flex-end; }
.pl-filter { display: flex; flex-direction: column; min-width: 140px; }
.pl-filter-grow { flex: 1; min-width: 200px; }
.pl-filter label { font-size: 10px; color: #94a3b8; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
.pl-filter input { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 9px 12px; font-weight: 600; outline: none; }

.pl-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 16px; }
.pl-metric { background: white; border-radius: 14px; padding: 16px; border: 1px solid #f1f5f9; display: flex; align-items: center; gap: 12px; }
.pl-metric-icon { width: 50px; height: 50px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 24px; flex-shrink: 0; color: white; }
.pl-1 .pl-metric-icon { background: linear-gradient(135deg, #16a34a, #15803d); }
.pl-2 .pl-metric-icon { background: linear-gradient(135deg, #e11d48, #be123c); }
.pl-3 .pl-metric-icon { background: linear-gradient(135deg, #2563eb, #1d4ed8); }
.pl-net-green .pl-metric-icon { background: linear-gradient(135deg, #10b981, #059669); }
.pl-net-red .pl-metric-icon { background: linear-gradient(135deg, #e11d48, #9f1239); }
.pl-metric-label { font-size: 10px; color: #94a3b8; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.pl-metric-value { font-size: 17px; font-weight: 800; color: #0f172a; }
.pl-net-green .pl-metric-value { color: #16a34a; }
.pl-net-red .pl-metric-value { color: #e11d48; }

.pl-chart-wrap, .pl-section { background: white; border-radius: 14px; padding: 16px; border: 1px solid #f1f5f9; margin-bottom: 16px; }
.pl-block-title { font-weight: 800; font-size: 14px; color: #0f172a; margin: 0 0 12px 0; }

.pl-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.pl-2col > * { margin-bottom: 0; }

.pl-mini { width: 100%; font-size: 14px; }
.pl-mini td { padding: 8px 4px; border-bottom: 1px solid #f1f5f9; }
.pl-mini-total td { border-top: 2px solid #0f172a; border-bottom: none; padding-top: 10px; }

.pl-table-wrap { overflow-x: auto; }
.pl-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.pl-table th { background: #f8fafc; padding: 10px; text-align: left; font-size: 11px; color: #64748b; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #e2e8f0; }
.pl-table td { padding: 11px 10px; border-bottom: 1px solid #f1f5f9; }
.pl-table tbody tr:hover { background: #faf8ff; }

.t-center { text-align: center; }
.t-right { text-align: right; font-variant-numeric: tabular-nums; }
.pl-strong { font-weight: 700; color: #0f172a; }
.pl-green { color: #16a34a; font-weight: 700; }
.pl-red { color: #e11d48; font-weight: 700; }
.pl-empty { text-align: center; color: #94a3b8; padding: 30px; font-style: italic; }
.pl-badge { background: #dbeafe; color: #2563eb; padding: 2px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; }

@media (max-width: 991px) {
  .pl-summary { grid-template-columns: repeat(2, 1fr); }
  .pl-2col { grid-template-columns: 1fr; }
}

@media print {
  .pl-page { background: white; padding: 0; height: auto; overflow: visible; }
  .pl-actions, .pl-filters, .pl-back { display: none !important; }
}
</style>
